import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';

export class SentryNetworkError extends Error {
  isAxiosError: boolean;
  config?: any;
  request?: any;
  response?: any;

  /**
   * @returns {string} - (예: [500 Error] /api/orders/{id})
   */
  private static generateName(error: AxiosError | any): string {
    const status = error.response?.status || 'No Response';
    const path = error.config?.url?.split('?')[0] || 'UnknownPath';

    const sanitizedPath = path.replace(/\/\d+(?=\/|$)/g, '/{id}');

    if (status >= 500) {
      return `[${status} Error] ${sanitizedPath}`;
    }
    if (status >= 400) {
      return `[${status} Error] ${sanitizedPath}`;
    }
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return `[Timeout] ${error.config?.url}`;
    }
    if (!error.response) {
      return `[Network Error] ${error.config?.url}`;
    }

    return `[ApiError] ${error.config?.url}`;
  }

  constructor(error: AxiosError | any, message?: string) {
    super(message ?? error.message);

    this.name = SentryNetworkError.generateName(error);

    this.isAxiosError = error.isAxiosError;
    this.config = error.config;
    this.request = error.request;
    this.response = error.response;
    this.stack = error.stack;
  }
}

export function reportErrorToSentry(error: SentryNetworkError | any) {
  Sentry.withScope((scope) => {
    scope.setFingerprint([
      error.name,
      error.config?.method?.toUpperCase(),
    ]);

    // Customized Tags
    scope.setTag('type', 'api');
    scope.setTag('api_name', error.name);
    if (error.response?.status) {
      scope.setTag('api_status', error.response.status);
    }

    // Level
    if (error.response?.status >= 500) {
      scope.setLevel('fatal');
    } else {
      scope.setLevel('warning');
    }

    // Context
    if (error.config) {
      const { method, url, params, data, headers } = error.config;
      scope.setContext('API Request Detail', {
        method,
        url,
        params,
        data: data ? JSON.parse(data) : 'No Data',
        headers,
      });
    }
    if (error.response) {
      const { status, data, headers } = error.response;
      scope.setContext('API Response Detail', { status, data, headers });
    }

    Sentry.captureException(error);
  });
}